import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase';

const formatDate = (value) => {
  if (!value) return '—';
  const date = value.toDate?.() || new Date(value);
  if (!(date instanceof Date) || isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-PR', {
    month: 'short',
    day: 'numeric',
  });
};

const SellerDashboard = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(() => auth.currentUser);
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [myProposals, setMyProposals] = useState([]);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      setUser(firebaseUser);
    });
    return unsubscribe;
  }, [auth]);

  useEffect(() => {
    let ignore = false;
    const fetchProfile = async () => {
      if (!user?.email) {
        if (!ignore) {
          setSellerProfile(null);
        }
        return;
      }
      try {
        const profileSnap = await getDocs(
          query(
            collection(db, 'teamMembers'),
            where('email', '==', user.email.toLowerCase()),
            limit(1)
          )
        );
        if (!ignore) {
          if (!profileSnap.empty) {
            const docSnap = profileSnap.docs[0];
            setSellerProfile({ id: docSnap.id, ...docSnap.data() });
          } else {
            setSellerProfile(null);
          }
        }
      } catch (err) {
        if (!ignore) {
          console.warn('No pudimos cargar el perfil del vendedor:', err);
          setSellerProfile(null);
        }
      } finally {
        // no-op
      }
    };

    fetchProfile();
    return () => {
      ignore = true;
    };
  }, [user?.email]);

  useEffect(() => {
    const fetchLeads = async () => {
      if (!user?.email && !user?.uid) return;
      setLoading(true);
      setError(null);
      try {
        const email = user?.email?.toLowerCase();
        const uid = user?.uid || null;
        const leadsRef = collection(db, 'leads');
        const proposalRef = collection(db, 'proposalTokens');

        const leadQueries = [];
        if (email) {
          leadQueries.push(query(leadsRef, where('assignedToEmail', '==', email)));
        }
        if (uid) {
          leadQueries.push(query(leadsRef, where('assignedToUid', '==', uid)));
        }

        const proposalQueries = [];
        if (email) {
          proposalQueries.push(query(proposalRef, where('createdBy.email', '==', email)));
        }
        if (uid) {
          proposalQueries.push(query(proposalRef, where('createdBy.uid', '==', uid)));
        }

        const [leadSnaps, proposalSnaps] = await Promise.all([
          Promise.all(leadQueries.map((q) => getDocs(q))),
          Promise.all(proposalQueries.map((q) => getDocs(q))),
        ]);

        const leadsMap = new Map();
        leadSnaps.forEach((snap) => {
          snap.forEach((docSnap) => {
            leadsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
          });
        });
        const leadsData = Array.from(leadsMap.values());

        const proposalsMap = new Map();
        proposalSnaps.forEach((snap) => {
          snap.forEach((docSnap) => {
            proposalsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
          });
        });
        const proposalsData = Array.from(proposalsMap.values());

        const recentData = [...leadsData]
          .sort(
            (a, b) =>
              (b.updatedAt?.toMillis?.() || b.updatedAt?.seconds || 0) -
              (a.updatedAt?.toMillis?.() || a.updatedAt?.seconds || 0)
          )
          .slice(0, 6);

        setAssignedLeads(leadsData);
        setRecentLeads(recentData);
        setMyProposals(
          proposalsData
            .sort(
              (a, b) =>
                (b.createdAt?.toMillis?.() || b.createdAt?.seconds || 0) -
                (a.createdAt?.toMillis?.() || a.createdAt?.seconds || 0)
            )
            .slice(0, 5)
        );
      } catch (err) {
        console.error('❌ Error cargando leads del vendedor:', err);
        setError('No pudimos cargar tus leads. Intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [user?.email, user?.uid]);

  const metrics = useMemo(() => {
    const total = assignedLeads.length;
    const won = assignedLeads.filter(
      (lead) => (lead.status || '').toLowerCase() === 'convertido'
    );
    const hot = assignedLeads.filter((lead) => (lead.aiScore || 0) >= 80);
    const active = total - won.length;
    const conversionRate = total ? Math.round((won.length / total) * 100) : 0;
    return {
      total,
      won: won.length,
      hot: hot.length,
      active,
      conversionRate,
    };
  }, [assignedLeads]);

  const pipeline = useMemo(() => {
    const buckets = {};
    assignedLeads.forEach((lead) => {
      const stage = (lead.pipelineStage || lead.status || 'nuevo').toLowerCase();
      buckets[stage] = (buckets[stage] || 0) + 1;
    });
    return Object.entries(buckets)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [assignedLeads]);

  const topOpportunities = useMemo(() => {
    return [...assignedLeads]
      .filter((lead) => (lead.aiScore || 0) > 0)
      .sort((a, b) => (b.aiScore || 0) - (a.aiScore || 0))
      .slice(0, 4);
  }, [assignedLeads]);

  if (!user) {
    return <p className="text-gray-600">Inicia sesión para ver tu panel.</p>;
  }

  const displayName =
    sellerProfile?.displayName ||
    sellerProfile?.name ||
    user.displayName ||
    user.email?.split('@')[0] ||
    'vendedor';
  const avatarUrl =
    sellerProfile?.photoURL ||
    sellerProfile?.photoUrl ||
    sellerProfile?.avatarUrl ||
    null;
  const avatarFallback = displayName.charAt(0)?.toUpperCase?.() || 'P';

  return (
    <div className="space-y-8">
      <section className="rounded-3xl bg-gradient-to-r from-emerald-600 to-emerald-500 p-6 text-white shadow">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-white/20 text-2xl font-semibold uppercase text-white flex items-center justify-center">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="h-16 w-16 rounded-2xl object-cover"
                />
              ) : (
                avatarFallback
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-emerald-50">
                Panel de ventas
              </p>
              <h2 className="text-2xl font-semibold">Hola, {displayName}</h2>
              <p className="text-emerald-50">
                {sellerProfile?.roleLabel || 'Equipo Puerto Rico Windows & Doors'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:w-1/2">
            <div className="rounded-2xl bg-white/15 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-emerald-100">
                Leads activos
              </p>
              <p className="text-2xl font-semibold">{metrics.active}</p>
            </div>
            <div className="rounded-2xl bg-white/15 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-emerald-100">
                Hot leads
              </p>
              <p className="text-2xl font-semibold">{metrics.hot}</p>
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded">
          {error}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Leads asignados</p>
          <p className="text-3xl font-semibold text-gray-900">{metrics.total}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">En proceso</p>
          <p className="text-3xl font-semibold text-blue-600">{metrics.active}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Hot leads</p>
          <p className="text-3xl font-semibold text-orange-500">{metrics.hot}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Tasa de cierre</p>
          <p className="text-3xl font-semibold text-emerald-600">
            {metrics.conversionRate}%
          </p>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline</h3>
          {pipeline.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aún no tienes leads con etapas asignadas.
            </p>
          ) : (
            <ul className="space-y-2">
              {pipeline.map(([stage, count]) => (
                <li
                  key={stage}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="capitalize text-gray-600">
                    {stage.replace('_', ' ')}
                  </span>
                  <span className="font-semibold text-gray-900">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Oportunidades prioridad
          </h3>
          {topOpportunities.length === 0 ? (
            <p className="text-sm text-gray-500">
              No hay leads con score de IA todavía.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {topOpportunities.map((lead) => (
                <li key={lead.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{lead.name}</p>
                    <p className="text-xs text-gray-500">{lead.businessType || lead.source}</p>
                  </div>
                  <span className="text-sm font-semibold text-orange-600">
                    Score {lead.aiScore}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="bg-white border border-orange-100 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Propuestas recientes</h3>
          {myProposals.length === 0 ? (
            <p className="text-sm text-gray-500">Aún no has enviado propuestas.</p>
          ) : (
            <ul className="space-y-3">
              {myProposals.map((proposal) => (
                <li
                  key={proposal.id}
                  className="rounded-2xl border border-orange-50 bg-orange-50/70 px-4 py-3 text-sm"
                >
                  <p className="font-semibold text-gray-900">
                    {proposal.clientInfo?.name || proposal.clientInfo?.email}
                  </p>
                  <p className="text-xs text-gray-600">
                    Total: ${Number(proposal.total || 0).toFixed(2)} · Estado:{' '}
                    {proposal.status || 'pendiente'}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border border-orange-100 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones rápidas</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { label: 'Mis leads', desc: 'Actualiza estatus y notas.', href: '/seller/leads' },
              { label: 'Propuestas', desc: 'Gestiona tus estimados.', href: '/seller/proposals' },
              { label: 'Facturas', desc: 'Revisa depósitos y balances.', href: '/seller/invoices' },
              { label: 'Clientes asignados', desc: 'Consulta historial y contactos.', href: '/seller/clients' },
            ].map((action) => (
              <button
                key={action.label}
                onClick={() => navigate(action.href)}
                className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-left text-sm text-gray-700 transition hover:bg-orange-100"
              >
                <p className="font-semibold text-gray-900">{action.label}</p>
                <p className="text-xs text-gray-600">{action.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mis leads</h3>
        {loading ? (
          <p className="text-sm text-gray-500">Cargando tus leads...</p>
        ) : assignedLeads.length === 0 ? (
          <p className="text-sm text-gray-500">Aún no tienes leads asignados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Lead</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Estado</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Último contacto</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {assignedLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{lead.name || '—'}</p>
                      <p className="text-gray-500">{lead.email}</p>
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-700">
                      {(lead.status || 'nuevo').replace('_', ' ')}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDate(lead.updatedAt || lead.lastContact)}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{lead.aiScore ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Últimos movimientos</h3>
        {recentLeads.length === 0 ? (
          <p className="text-sm text-gray-500">
            Aquí verás los leads más recientes asignados o actualizados.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {recentLeads.map((lead) => (
              <li key={lead.id} className="py-3 flex justify-between text-sm">
                <div>
                  <p className="font-medium text-gray-900">{lead.name}</p>
                  <p className="text-xs text-gray-500">
                    {(lead.pipelineStage || lead.status || 'nuevo').replace('_', ' ')}
                  </p>
                </div>
                <span className="text-gray-500">{formatDate(lead.updatedAt || lead.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default SellerDashboard;
